import { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { recommendLocal } from "@/lib/recommend";
import { MENTIONS_LEGALES } from "@/lib/legal";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import LeadForm from "@/components/LeadForm";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Briefcase, FileText, CheckCircle2, AlertTriangle, XCircle,
  Clock, Download, RotateCcw, Printer, ArrowLeft, Wallet, Building2, Tractor,
  BadgeCheck, ChevronRight, Sparkles, Landmark, HeartHandshake, Award, Calculator, Mail,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import FautrasLogo from "@/components/FautrasLogo";
import { ApplyDevisButton, DevisBanner, DevisPrixWarning, DEVIS_PRIX, devisAnswers, taegFrom } from "@/components/ApplyDevisButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reformate "VERDIER Jérôme" -> "Jérôme Verdier" (nom en MAJUSCULES déplacé à la fin, en Capitale).
const formatVendeurName = (name) => {
  if (!name) return "";
  const tokens = String(name).trim().split(/\s+/);
  const isUpper = (t) => t.length > 1 && t === t.toUpperCase() && /[A-ZÀ-Ÿ]/.test(t);
  const surname = tokens.filter(isUpper);
  const given = tokens.filter((t) => !isUpper(t));
  if (surname.length === 0 || given.length === 0) return name;
  const cap = (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  return [...given, ...surname.map(cap)].join(" ");
};

const initialAnswers = {
  client_type: null,
  situation: null,
  resident_fr: true,
  annee_naissance: "2008",
  pro_anciennete_2ans: null,
  affilie_msa: null,
  lien_equestre: null,
  detention: null,
  banque: null,
  code_postal: "",
  etat_vehicule: null,
  annee_vehicule: "",
  vehicule_moins_5ans: null,
  tva_recuperable: null,
  prix_remorque: "",
  apport_eur: "",
  montant: "",
  apport_auto: false,
  apport_touched: false,
};

const anneeCourante = new Date().getFullYear();
const moins5ans = (annee) => anneeCourante - parseInt(annee, 10) < 5;
const anneeValide = (annee) => /^\d{4}$/.test(String(annee)) && +annee >= 1950 && +annee <= anneeCourante;
const etatVehiculeOk = (a) => a.etat_vehicule === "neuf" ||
  (a.etat_vehicule === "occasion" && (anneeValide(a.annee_vehicule) || (!a.annee_vehicule && a.vehicule_moins_5ans != null)));

const cpValide = (cp) => /^\d{5}$/.test(String(cp || "").replace(/\s/g, ""));

function CreditMutuelCp({ answers, set, onNext }) {
  const ok = cpValide(answers.code_postal);
  return (
    <div className="mt-3 rounded-2xl border border-[#e0d3ba] bg-[#faf7f1] p-5" data-testid="cm-cp-block">
      <Label htmlFor="cm-cp" className="text-sm font-semibold">Code postal de votre exploitation *</Label>
      <p className="text-xs text-muted-foreground mt-1">Le barème Crédit Mutuel dépend du département.</p>
      <Input id="cm-cp" inputMode="numeric" maxLength={5} placeholder="ex. 24000" data-testid="cm-cp-input"
        value={answers.code_postal} onChange={(e) => set({ code_postal: e.target.value.replace(/\D/g, "").slice(0, 5) })}
        className="mt-3 h-12 bg-white max-w-[200px]" />
      <Button type="button" disabled={!ok} onClick={onNext} data-testid="cm-cp-next"
        className="mt-4 h-11 rounded-full bg-[#9E7B41] hover:bg-[#876634] px-6">
        Continuer <ChevronRight className="h-4 w-4 ml-1" />
      </Button>
    </div>
  );
}

function EtatVehiculeField({ answers, set }) {
  const choose = (etat) => answers.etat_vehicule !== etat && set({ etat_vehicule: etat, vehicule_moins_5ans: null, annee_vehicule: "", tva_recuperable: null });
  const onAnnee = (v) => set({ annee_vehicule: v, vehicule_moins_5ans: anneeValide(v) ? moins5ans(v) : null, tva_recuperable: null });
  const opt = (v, label) => (
    <label key={v} data-testid={`etat-${v}-option`}
      className={`flex items-center gap-2.5 rounded-xl border-2 bg-white px-4 py-3 cursor-pointer transition-colors ${answers.etat_vehicule === v ? "border-[#9E7B41] bg-[#faf7f1]" : "border-border hover:border-[#9E7B41]/50"}`}>
      <Checkbox checked={answers.etat_vehicule === v} onCheckedChange={() => choose(v)} data-testid={`etat-${v}-checkbox`}
        className="data-[state=checked]:bg-[#9E7B41] data-[state=checked]:border-[#9E7B41]" />
      <span className="text-sm font-semibold text-[#2b2b2b]">{label}</span>
    </label>
  );
  return (
    <div>
      <Label className="text-sm font-semibold">État du véhicule *</Label>
      <div className="mt-2 grid grid-cols-2 gap-3">
        {opt("neuf", "Véhicule neuf")}
        {opt("occasion", "Véhicule d'occasion")}
      </div>
      {answers.etat_vehicule === "occasion" && (
        <div className="mt-3">
          <Label htmlFor="annee-vehicule" className="text-sm font-semibold">Année du véhicule *</Label>
          <Input id="annee-vehicule" type="number" inputMode="numeric" min="1950" max={anneeCourante} placeholder="ex. 2022"
            data-testid="annee-vehicule-input" value={answers.annee_vehicule} onChange={(e) => onAnnee(e.target.value)}
            className="mt-2 h-12 bg-white max-w-[200px]" />
          {anneeValide(answers.annee_vehicule) && (
            <p className="text-xs text-muted-foreground mt-1.5" data-testid="annee-vehicule-age">
              {answers.vehicule_moins_5ans ? "Moins de 5 ans" : "5 ans ou plus"} : certaines solutions en dépendent.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function computeSteps(a, needLead) {
  // Arrivée depuis le configurateur de devis : client résident français et majeur → page projet directement.
  const steps = a.depuis_devis ? [] : ["naissance"];

  if (!a.depuis_devis) {
    // Non résident français : non éligible → directement au résultat.
    if (a.resident_fr === false) return ["naissance", "result"];

    const yr = parseInt(a.annee_naissance, 10);
    const anneeOk = a.annee_naissance && yr >= 1900 && yr <= new Date().getFullYear();
    if (!anneeOk) return steps;

    // Âge hors 18 – 81 ans : aucun financement possible, on va directement au résultat.
    const age = new Date().getFullYear() - yr;
    if (age < 18 || age > 81) return ["naissance", "result"];
  }

  steps.push("montant");
  const filled = a.montant !== "" && a.montant != null && !Number.isNaN(parseFloat(a.montant));
  if (!filled) return steps;

  const big = parseFloat(a.montant) > 3000; // au-dessus du seuil de 3 000 €
  steps.push("client_type");

  const isParticulierFlow = a.client_type === "particulier" || a.pro_anciennete_2ans === false;

  if (a.client_type === "particulier") {
    steps.push("particulier_profil");
  } else if (a.client_type === "pro") {
    steps.push("pro_anciennete");
    // Pro < 2 ans : pas d'étape "Situation du particulier" (situation pré-remplie = pro_moins_2ans),
    // on passe directement à l'état de la remorque / au résultat.
    if (a.pro_anciennete_2ans === true && big) {
      steps.push("pro_msa");
      if (a.affilie_msa === true) steps.push("pro_banque");
      else if (a.affilie_msa === false) steps.push("pro_lien_equestre");
    }
  }

  // Le montant ≤ 3 000 € ne dépend ni de l'état du véhicule ni de la durée de détention.
  // CDD / sans emploi : on s'arrête au paiement en 2/3/4 fois → pas de questions véhicule/détention.
  const situationBloquee = a.situation === "cdd" || a.situation === "chomage";
  if (big && a.client_type && !situationBloquee) {
    // L'état du véhicule (neuf / occasion + année) est demandé sur la page « Montant à financer ».
    if (isParticulierFlow && a.etat_vehicule === "neuf") steps.push("detention");

    // Pro (2 ans ou +) AFFILIÉ MSA avec van d'occasion de moins de 5 ans : question obligatoire sur la TVA
    // récupérable → conditionne la règle de TVA non finançable des crédits bancaires MSA.
    // (Hors MSA : le crédit-bail finance le TTC, la question est inutile.)
    const isProDeuxAns = a.client_type === "pro" && a.pro_anciennete_2ans === true;
    if (isProDeuxAns && a.affilie_msa === true && a.etat_vehicule === "occasion" && a.vehicule_moins_5ans === true) {
      steps.push("tva_recup");
    }

    // Pro MSA sans crédit bancaire disponible (banque non valide ou occasion > 5 ans) :
    // la durée de détention décide Sofinco (garder longtemps) vs crédit-bail (renouveler).
    if (a.client_type === "pro" && a.pro_anciennete_2ans === true && a.affilie_msa === true && a.etat_vehicule) {
      const banks = ["credit_mutuel", "credit_agricole", "banque_populaire"];
      const vehOk = a.etat_vehicule === "neuf" || (a.etat_vehicule === "occasion" && a.vehicule_moins_5ans === true);
      const bankPossible = banks.includes(a.banque) && vehOk;
      // Occasion de plus de 5 ans : ni crédit MSA ni crédit-bail → seul Sofinco reste,
      // inutile de poser la question de la durée de détention.
      const occasionVieille = a.etat_vehicule === "occasion" && a.vehicule_moins_5ans === false;
      if (!bankPossible && !occasionVieille) steps.push("detention");
    }
  }

  if (a.client_type && needLead) steps.push("lead");
  if (a.client_type) steps.push("result");
  return steps;
}

const OptionCard = ({ icon: Icon, title, desc, selected, onClick, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`card-hover group text-left w-full rounded-2xl border-2 bg-white p-5 sm:p-6 flex items-start gap-4 ${
      selected ? "border-[#9E7B41] ring-2 ring-[#9E7B41]/20" : "border-border"
    }`}
  >
    <span
      className={`shrink-0 grid place-items-center h-12 w-12 rounded-xl ${
        selected ? "bg-[#9E7B41] text-white" : "bg-[#f3ede2] text-[#9E7B41]"
      } transition-colors`}
    >
      <Icon className="h-6 w-6" />
    </span>
    <span className="flex-1">
      <span className="block font-heading font-bold text-base sm:text-lg text-[#2b2b2b]">{title}</span>
      {desc && <span className="block text-sm text-muted-foreground mt-1 leading-relaxed">{desc}</span>}
    </span>
    <ChevronRight className="h-5 w-5 text-muted-foreground/50 group-hover:text-[#9E7B41] mt-1 transition-colors" />
  </button>
);

function AmountsBar({ answers, set }) {
  const onPrix = (v) => {
    const p = parseFloat(v);
    if (Number.isNaN(p)) { set({ prix_remorque: v }); return; }
    if (answers.apport_touched) {
      const ap = parseFloat(answers.apport_eur) || 0;
      set({ prix_remorque: v, montant: String(Math.max(p - ap, 0)) });
    } else {
      const ap = Math.round(p * 0.20);
      set({ prix_remorque: v, apport_eur: String(ap), apport_auto: false, montant: String(Math.max(p - ap, 0)) });
    }
  };
  const onApport = (v) => {
    const p = parseFloat(answers.prix_remorque);
    let a = parseFloat(v) || 0;
    if (!Number.isNaN(p) && a > p) a = p; // l'apport ne peut dépasser le prix
    const val = (!Number.isNaN(p) && (parseFloat(v) || 0) > p) ? String(a) : v;
    set({ apport_eur: val, apport_auto: false, apport_touched: true, montant: !Number.isNaN(p) ? String(Math.max(p - a, 0)) : answers.montant });
  };
  const onMontant = (v) => {
    const prix = parseFloat(answers.prix_remorque);
    let m = parseFloat(v);
    if (!Number.isNaN(prix) && !Number.isNaN(m)) {
      if (m > prix) m = prix; // le montant à financer ne peut dépasser le prix
      set({ montant: String(m), apport_eur: String(Math.max(prix - m, 0)), apport_auto: false, apport_touched: true });
    } else set({ montant: v, apport_auto: false, apport_touched: true });
  };
  return (
    <div className="border-t border-border bg-[#faf7f1]/90 backdrop-blur-xl" data-testid="amounts-bar">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-2.5 flex items-end gap-2.5 sm:gap-3">
        <div className="flex-1 min-w-0">
          <Label className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Prix remorque TTC (€)</Label>
          <Input type="number" min="0" data-testid="bar-prix-input" value={answers.prix_remorque}
            onChange={(e) => onPrix(e.target.value)} className="mt-1 h-10 bg-white" />
        </div>
        <div className="flex-1 min-w-0">
          <Label className={`text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide ${answers.apport_auto ? "text-red-600" : "text-muted-foreground"}`}>Apport (€)</Label>
          <Input type="number" min="0" data-testid="bar-apport-input" value={answers.apport_eur}
            onChange={(e) => onApport(e.target.value)}
            className={`mt-1 h-10 bg-white ${answers.apport_auto ? "text-red-600 font-bold border-red-400 focus-visible:ring-red-300" : ""}`} />
        </div>
        <div className="flex-1 min-w-0">
          <Label className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-[#9E7B41] flex items-center gap-1">
            <Wallet className="h-3 w-3" /> À financer (€)
          </Label>
          <Input type="number" min="0" data-testid="bar-montant-input" value={answers.montant}
            onChange={(e) => onMontant(e.target.value)} className="mt-1 h-10 font-bold bg-white border-[#9E7B41]/50 focus-visible:ring-[#9E7B41]/30" />
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 -mt-1 pb-2 empty:hidden">
        <DevisPrixWarning prix={answers.prix_remorque} onRestore={() => onPrix(String(DEVIS_PRIX))} />
      </div>
    </div>
  );
}

const StepShell = ({ eyebrow, title, subtitle, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.32, ease: "easeOut" }}
    className="w-full"
  >
    <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#9E7B41] mb-2">{eyebrow}</p>
    <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#2b2b2b] tracking-tight">{title}</h2>
    {subtitle && <p className="text-muted-foreground mt-2 text-sm sm:text-base max-w-xl">{subtitle}</p>}
    <div className="divider-gold my-5" />
    {children}
  </motion.div>
);

export default function Assistant() {
  const { user, logout } = useAuth();
  const [started, setStarted] = useState(() => !!devisAnswers(initialAnswers));
  const [answers, setAnswers] = useState(() => devisAnswers(initialAnswers) || initialAnswers);
  const [stepIndex, setStepIndex] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leadVendeur, setLeadVendeur] = useState(null);
  const [leadSubmitted, setLeadSubmitted] = useState(() => sessionStorage.getItem("fautras_lead_done") === "1");

  const needLead = !user && !leadSubmitted;
  const steps = useMemo(() => computeSteps(answers, needLead), [answers, needLead]);
  const currentKey = steps[stepIndex] || steps[0];
  const progress = Math.round((stepIndex / (steps.length - 1)) * 100);

  const set = (patch) => setAnswers((p) => ({ ...p, ...patch }));

  const onPrixStep = (prix) => {
    const p = parseFloat(prix);
    if (Number.isNaN(p)) { set({ prix_remorque: prix }); return; }
    if (answers.apport_touched) {
      const ap = parseFloat(answers.apport_eur) || 0;
      set({ prix_remorque: prix, montant: String(Math.max(p - ap, 0)) });
    } else {
      // Apport pré-rempli par défaut à 20% du prix (modifiable ensuite).
      const ap = Math.round(p * 0.20);
      set({ prix_remorque: prix, apport_eur: String(ap), apport_auto: false, montant: String(Math.max(p - ap, 0)) });
    }
  };

  const goNext = () => {
    const ns = computeSteps(answers, needLead);
    setStepIndex((i) => Math.min(i + 1, ns.length - 1));
  };
  const goBack = () => {
    if (stepIndex === 0) {
      if (answers.depuis_devis) return;
      setStarted(false);
      return;
    }
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const selectAndNext = (patch) => {
    set(patch);
    const merged = { ...answers, ...patch };
    const ns = computeSteps(merged, needLead);
    setTimeout(() => setStepIndex((i) => Math.min(i + 1, ns.length - 1)), 180);
  };

  const buildPayload = (a, dureeMois) => ({
    client_type: a.client_type,
    resident_fr: a.resident_fr,
    situation: a.situation || null,
    age: a.annee_naissance ? (new Date().getFullYear() - parseInt(a.annee_naissance, 10)) : null,
    pro_anciennete_2ans: a.pro_anciennete_2ans,
    affilie_msa: a.affilie_msa,
    lien_equestre: a.lien_equestre,
    banque: a.banque || null,
    etat_vehicule: a.etat_vehicule || null,
    detention: a.detention || null,
    vehicule_moins_5ans: a.vehicule_moins_5ans,
    montant: a.montant ? parseFloat(a.montant) : 0,
    prix_remorque: a.prix_remorque ? parseFloat(a.prix_remorque) : null,
    apport: a.apport_eur ? parseFloat(a.apport_eur) : null,
    duree_mois: dureeMois || null,
    code_postal: a.code_postal || null,
  });

  const fetchRecommendation = async (a) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/recommend`, buildPayload(a));
      setResult(data);
    } catch (e) {
      // Repli HORS-LIGNE : moteur miroir + catalogue mis en cache.
      const raw = localStorage.getItem("fautras_catalog");
      if (raw) {
        try {
          setResult(recommendLocal(buildPayload(a), JSON.parse(raw)));
          toast.message("Mode hors-ligne : recommandation calculée sans connexion.");
        } catch (err) {
          toast.error("Erreur lors du calcul de la recommandation.");
        }
      } else {
        toast.error("Hors-ligne et données non encore chargées. Ouvrez l'app une fois avec du réseau.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentKey === "result") fetchRecommendation(answers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey]);

  // Met en cache le catalogue (solutions + règles) pour le repli hors-ligne.
  useEffect(() => {
    axios.get(`${API}/config`).then(({ data }) => {
      const solutionsById = {};
      (data.solutions || []).forEach((s) => { solutionsById[s.id] = s; });
      localStorage.setItem("fautras_catalog", JSON.stringify({
        seuil: data.seuil, solutionsById, eligibility_rules: data.eligibility_rules || [],
      }));
    }).catch(() => {});
  }, []);

  // Recalcul en direct quand les montants sont modifiés depuis le bandeau sur la page résultat.
  useEffect(() => {
    if (currentKey !== "result") return;
    const t = setTimeout(() => fetchRecommendation(answers), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.montant, answers.apport_eur, answers.prix_remorque]);

  // Le parcours peut se raccourcir/rallonger quand le montant change de palier (seuil 3 000 €).
  useEffect(() => {
    setStepIndex((i) => Math.min(i, steps.length - 1));
  }, [steps.length]);

  // Un vendeur/admin peut rouvrir la simulation d'un client depuis "Mes clients".
  useEffect(() => {
    const raw = sessionStorage.getItem("fautras_prefill");
    if (!raw) return;
    try {
      const a = JSON.parse(raw);
      setAnswers(a);
      setStarted(true);
      setLeadSubmitted(true);
      const ns = computeSteps(a, false);
      const ri = ns.indexOf("result");
      setStepIndex(ri >= 0 ? ri : ns.length - 1);
    } catch (err) {
      console.error("Reprise de simulation impossible :", err);
    }
    // Suppression différée : survit au double-montage React StrictMode (dev).
    const t = setTimeout(() => sessionStorage.removeItem("fautras_prefill"), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const restart = () => {
    const d = devisAnswers(initialAnswers);
    setAnswers(d || initialAnswers);
    setResult(null);
    setStepIndex(0);
    setStarted(!!d);
    setLeadVendeur(null);
    window.scrollTo({ top: 0 });
  };

  const downloadFile = async (kind, dureeMois) => {
    toast.success(kind === "pdf" ? "Génération de la fiche PDF…" : "Génération de l'Excel…");
    try {
      const res = await axios.post(`${API}/export/${kind}`, buildPayload(answers, dureeMois), { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = kind === "pdf" ? "Fautras_recap_client.pdf" : "Fautras_recap_client.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erreur lors de la génération du document.");
    }
  };
  const downloadExcel = (dureeMois) => downloadFile("excel", dureeMois);
  const downloadPdf = (dureeMois) => downloadFile("pdf", dureeMois);

  // ---------------- LANDING ----------------
  if (!started) {
    return (
      <div className="min-h-screen">
        <div className="hero-fautras relative overflow-hidden">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 pb-20 sm:pb-28 relative z-10">
            <div className="flex items-center justify-between">
              <FautrasLogo dark />
              <div className="flex items-center gap-2 sm:gap-3">
                {user ? (
                  <>
                    <span className="text-xs text-white/60 hidden sm:inline" data-testid="hero-user-email">{user.email}</span>
                    {user.role === "admin" ? (
                      <Link to="/admin" data-testid="hero-admin-link"
                        className="text-sm font-semibold text-white/70 hover:text-white px-3 py-2 rounded-lg transition-colors">Admin</Link>
                    ) : (
                      <Link to="/mes-clients" data-testid="hero-mesclients-link"
                        className="text-sm font-semibold text-white/70 hover:text-white px-3 py-2 rounded-lg transition-colors">Mes clients</Link>
                    )}
                    <Link to="/compte" data-testid="hero-compte-link"
                      className="text-sm font-semibold text-white/70 hover:text-white px-3 py-2 rounded-lg transition-colors">Mon compte</Link>
                    <button onClick={logout} data-testid="hero-logout"
                      className="text-sm font-semibold text-white/70 hover:text-white px-3 py-2 rounded-lg transition-colors">Déconnexion</button>
                  </>
                ) : (
                  <Link to="/login" data-testid="hero-vendeur-login"
                    className="text-sm font-semibold text-white/70 hover:text-white px-3 py-2 rounded-lg transition-colors">
                    Espace vendeur
                  </Link>
                )}
              </div>
            </div>

            <div className="mt-16 sm:mt-24 max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-[#d6bd93] mb-5">
                <Sparkles className="h-4 w-4" /> Simulateur de financement
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
                Trouvez le financement<br />idéal pour votre van ou remorque.
              </h1>
              <p className="text-white/70 mt-6 text-base sm:text-lg leading-relaxed max-w-xl">
                Répondez à quelques questions sur votre situation et votre projet :
                nous vous recommandons la solution de financement adaptée et la liste des pièces à préparer.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  data-testid="start-button"
                  onClick={() => { setStarted(true); setStepIndex(0); }}
                  className="bg-[#9E7B41] hover:bg-[#876634] text-white text-base h-13 px-8 py-6 rounded-xl font-semibold"
                >
                  Démarrer la simulation <ChevronRight className="h-5 w-5 ml-1" />
                </Button>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-white/65 text-sm" data-testid="reassurance-row">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#d6bd93]" /> Simulation gratuite</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#d6bd93]" /> Sans engagement</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#d6bd93]" /> En 2 minutes</span>
              </div>
              <p className="text-white/40 text-xs mt-8 tracking-wider">VANS TRACTÉS DEPUIS 1992</p>
            </div>
          </div>
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
               style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "26px 26px" }} />
        </div>

        <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-12 sm:-mt-16 relative z-20 pb-20">
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: User, t: "Votre situation", d: "Particulier ou professionnel, régime MSA, situation et âge." },
              { icon: Landmark, t: "Solution adaptée", d: "Sofinco, Socram, Mutualease, banques agricoles." },
              { icon: FileText, t: "Pièces à préparer", d: "La liste exacte à réunir pour votre dossier." },
            ].map((c, i) => (
              <motion.div
                key={c.t}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.09 }}
                className="bg-white rounded-2xl border border-border p-6 shadow-sm"
              >
                <div className="h-11 w-11 rounded-xl bg-[#f3ede2] text-[#9E7B41] grid place-items-center mb-4">
                  <c.icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading font-bold text-[#2b2b2b]">{c.t}</h3>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---------------- WIZARD ----------------
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-border">
        {answers.depuis_devis && <DevisBanner />}
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button type="button" onClick={restart} data-testid="logo-home-button"
            className="transition-opacity hover:opacity-80 cursor-pointer" title="Retour à l'accueil">
            <FautrasLogo />
          </button>
          <div className="flex items-center gap-1 sm:gap-2">
            {user ? (
              <>
                {user.role === "admin" ? (
                  <Link to="/admin" data-testid="wizard-admin-link"
                    className="text-xs sm:text-sm font-semibold text-muted-foreground hover:text-[#9E7B41] px-2 py-1.5 rounded-lg transition-colors">Admin</Link>
                ) : (
                  <Link to="/mes-clients" data-testid="wizard-mesclients-link"
                    className="text-xs sm:text-sm font-semibold text-muted-foreground hover:text-[#9E7B41] px-2 py-1.5 rounded-lg transition-colors">Mes clients</Link>
                )}
                <Link to="/compte" data-testid="wizard-compte-link"
                  className="text-xs sm:text-sm font-semibold text-muted-foreground hover:text-[#9E7B41] px-2 py-1.5 rounded-lg transition-colors hidden sm:inline">Mon compte</Link>
                <button onClick={logout} data-testid="wizard-logout"
                  className="text-xs sm:text-sm font-semibold text-muted-foreground hover:text-[#9E7B41] px-2 py-1.5 rounded-lg transition-colors hidden sm:inline">Déconnexion</button>
                <span className="h-4 w-px bg-border mx-1 hidden sm:inline" />
              </>
            ) : (
              <>
                <Link to="/login" data-testid="wizard-vendeur-login"
                  className="text-xs sm:text-sm font-semibold text-muted-foreground hover:text-[#9E7B41] px-2 py-1.5 rounded-lg transition-colors">Espace vendeur</Link>
                <span className="h-4 w-px bg-border mx-1 hidden sm:inline" />
              </>
            )}
            <Button
              variant="ghost" size="sm" data-testid="back-button" onClick={goBack}
              className="text-muted-foreground hover:text-[#9E7B41]"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Retour
            </Button>
          </div>
        </div>
        {currentKey !== "result" && (
          <div className="max-w-3xl mx-auto px-5 sm:px-8 pb-3">
            <Progress value={progress} className="h-1.5" data-testid="wizard-progress" />
          </div>
        )}
        {currentKey !== "montant" && answers.montant !== "" && answers.montant != null && (
          <AmountsBar answers={answers} set={set} />
        )}
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-6 sm:py-8">
        <AnimatePresence mode="wait">

          {currentKey === "naissance" && (
            <StepShell key="naissance" eyebrow="Étape 1" title="Vos informations"
              subtitle="Votre résidence et votre année de naissance : ces informations conditionnent l'accès au financement.">
              <div className="mb-6">
                <Label className="text-sm font-semibold">Êtes-vous résident français ?</Label>
                <div className="mt-2 grid grid-cols-2 gap-3 max-w-xs">
                  <button type="button" data-testid="resident-oui"
                    onClick={() => set({ resident_fr: true })}
                    className={`rounded-xl border-2 py-3 text-sm font-semibold transition-colors ${
                      answers.resident_fr === true ? "border-[#9E7B41] bg-white text-[#2b2b2b]" : "border-border bg-white/60 text-muted-foreground"
                    }`}>
                    Oui
                  </button>
                  <button type="button" data-testid="resident-non"
                    onClick={() => set({ resident_fr: false })}
                    className={`rounded-xl border-2 py-3 text-sm font-semibold transition-colors ${
                      answers.resident_fr === false ? "border-red-400 bg-red-50 text-red-700" : "border-border bg-white/60 text-muted-foreground"
                    }`}>
                    Non
                  </button>
                </div>
                {answers.resident_fr === false && (
                  <p className="text-xs text-red-600 mt-2 font-medium">
                    Sans résidence en France, le financement n'est pas possible.
                  </p>
                )}
              </div>

              <div className="max-w-xs">
                <Label htmlFor="naissance" className="text-sm font-semibold">Année de naissance</Label>
                <Input id="naissance" type="number" min="1900" max={new Date().getFullYear()}
                  data-testid="naissance-input" placeholder="ex : 1985" value={answers.annee_naissance}
                  onChange={(e) => set({ annee_naissance: e.target.value })}
                  disabled={answers.resident_fr === false}
                  className="mt-2 h-14 text-lg font-bold disabled:opacity-50" />
                {(() => {
                  const yr = parseInt(answers.annee_naissance, 10);
                  const ok = answers.annee_naissance && yr >= 1900 && yr <= new Date().getFullYear();
                  if (!ok || answers.resident_fr === false) return null;
                  return <p className="text-xs text-muted-foreground mt-2">Soit environ {new Date().getFullYear() - yr} ans.</p>;
                })()}
              </div>
              <Button data-testid="naissance-next-button"
                disabled={(() => {
                  if (answers.resident_fr === false) return false; // on peut continuer vers le résultat (non éligible)
                  const yr = parseInt(answers.annee_naissance, 10);
                  return !(answers.annee_naissance && yr >= 1900 && yr <= new Date().getFullYear());
                })()}
                onClick={goNext} className="mt-8 bg-[#9E7B41] hover:bg-[#876634] h-12 px-7 rounded-xl font-semibold">
                Continuer <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </StepShell>
          )}

          {currentKey === "client_type" && (
            <StepShell key="client_type" eyebrow="Étape 2" title="Votre profil"
              subtitle="Quel est votre statut pour l'achat de votre van ou remorque Fautras ?">
              <div className="grid gap-4">
                <OptionCard icon={User} testId="client-type-particulier-card"
                  title="Particulier" desc="Achat à titre privé (loisir, balade, compétition avec un van chevaux ; ou usage personnel d'une remorque fourgon)."
                  selected={answers.client_type === "particulier"}
                  onClick={() => selectAndNext({ client_type: "particulier", pro_anciennete_2ans: null, affilie_msa: null, banque: null })} />
                <OptionCard icon={Briefcase} testId="client-type-pro-card"
                  title="Professionnel" desc="Activité professionnelle (centre équestre, élevage, écurie avec un van ; ou artisan, commerçant, auto-école, food truck avec une remorque fourgon)."
                  selected={answers.client_type === "pro"}
                  onClick={() => selectAndNext({ client_type: "pro", situation: null })} />
              </div>
            </StepShell>
          )}

          {currentKey === "particulier_profil" && (
            <StepShell key="particulier_profil" eyebrow="Situation" title="Votre situation"
              subtitle="Quelle est votre situation professionnelle ?">
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { v: "cdi", t: "CDI" },
                  { v: "profession_liberale", t: "Profession libérale" },
                  { v: "gerant", t: "Gérant de société" },
                  { v: "retraite", t: "Retraité" },
                  { v: "auto_entrepreneur", t: "Auto-entrepreneur" },
                  { v: "association", t: "Association" },
                  { v: "pro_moins_2ans", t: "Professionnel (– de 2 ans d'activité)" },
                  { v: "cdd", t: "CDD" },
                  { v: "chomage", t: "Sans emploi" },
                ].map((s) => (
                  <button key={s.v} type="button" data-testid={`situation-${s.v}-option`}
                    onClick={() => selectAndNext({ situation: s.v })}
                    className={`card-hover rounded-xl border-2 bg-white px-4 py-4 text-left flex items-center justify-between ${
                      answers.situation === s.v ? "border-[#9E7B41] ring-2 ring-[#9E7B41]/20" : "border-border"
                    }`}>
                    <span className="font-semibold text-[#2b2b2b]">{s.t}</span>
                    {answers.situation === s.v && <BadgeCheck className="h-5 w-5 text-[#9E7B41]" />}
                  </button>
                ))}
              </div>
            </StepShell>
          )}

          {currentKey === "pro_anciennete" && (
            <StepShell key="pro_anciennete" eyebrow="Étape 2" title="Ancienneté de votre activité"
              subtitle="Un professionnel doit justifier de 2 ans d'activité ou plus pour être finançable.">
              <div className="grid gap-4">
                <OptionCard icon={CheckCircle2} testId="anciennete-oui-card"
                  title="2 ans d'activité ou plus" desc="Vous remplissez la condition d'ancienneté."
                  selected={answers.pro_anciennete_2ans === true}
                  onClick={() => selectAndNext({ pro_anciennete_2ans: true })} />
                <OptionCard icon={XCircle} testId="anciennete-non-card"
                  title="Moins de 2 ans d'activité" desc="Orientation automatique vers un financement particulier."
                  selected={answers.pro_anciennete_2ans === false}
                  onClick={() => selectAndNext({ pro_anciennete_2ans: false, situation: "pro_moins_2ans", affilie_msa: null, banque: null, lien_equestre: null })} />
              </div>
            </StepShell>
          )}

          {currentKey === "pro_msa" && (
            <StepShell key="pro_msa" eyebrow="Étape 3" title="Affiliation MSA"
              subtitle="Êtes-vous affilié à la MSA (exploitant agricole) ? Cela ouvre des crédits agricoles avantageux.">
              <div className="grid gap-4">
                <OptionCard icon={Tractor} testId="msa-oui-card"
                  title="Affilié MSA" desc="Exploitant agricole — accès aux crédits agricoles selon la banque."
                  selected={answers.affilie_msa === true}
                  onClick={() => selectAndNext({ affilie_msa: true })} />
                <OptionCard icon={Building2} testId="msa-non-card"
                  title="Hors MSA" desc="Professionnel non agricole."
                  selected={answers.affilie_msa === false}
                  onClick={() => selectAndNext({ affilie_msa: false, banque: null, lien_equestre: null })} />
              </div>
            </StepShell>
          )}

          {currentKey === "pro_lien_equestre" && (
            <StepShell key="pro_lien_equestre" eyebrow="Étape 4" title="Activité liée à l'usage de la remorque"
              subtitle="L'activité professionnelle a-t-elle un lien avec l'usage de la remorque (pour un van : centre équestre, élevage, écurie, transport de chevaux… ; pour une remorque fourgon : transport de matériel ou marchandise, auto-école, artisan, food truck, etc.) ?">
              <div className="grid gap-4">
                <OptionCard icon={Award} testId="equestre-oui-card"
                  title="Oui, usage de la remorque en lien avec l'activité" desc="Orientation vers le Crédit-bail Mutualease."
                  selected={answers.lien_equestre === true}
                  onClick={() => selectAndNext({ lien_equestre: true })} />
                <OptionCard icon={Building2} testId="equestre-non-card"
                  title="Non, usage de la remorque sans lien avec l'activité" desc="Orientation vers le crédit classique Sofinco."
                  selected={answers.lien_equestre === false}
                  onClick={() => selectAndNext({ lien_equestre: false })} />
              </div>
            </StepShell>
          )}

          {currentKey === "pro_banque" && (
            <StepShell key="pro_banque" eyebrow="Étape 4" title="Votre banque"
              subtitle="Le crédit agricole proposé dépend de votre banque.">
              <div className="grid gap-3">
                {[
                  { v: "credit_mutuel", t: "Crédit Mutuel", d: "Crédit CLEMAT ou ACTIMAT / CICAGRI selon votre département" },
                  { v: "credit_agricole", t: "Crédit Agricole", d: "Crédit AGILOR" },
                  { v: "banque_populaire", t: "Banque Populaire", d: "Crédit AGRILISMAT" },
                  { v: "autre", t: "Autre / non précisée", d: "Afficher les 3 options agricoles" },
                ].map((b) => (
                  <div key={b.v}>
                    <OptionCard icon={Landmark} testId={`banque-${b.v}-card`}
                      title={b.t} desc={b.d} selected={answers.banque === b.v}
                      onClick={() => (b.v === "credit_mutuel" ? set({ banque: b.v }) : selectAndNext({ banque: b.v }))} />
                    {b.v === "credit_mutuel" && answers.banque === "credit_mutuel" && (
                      <CreditMutuelCp answers={answers} set={set} onNext={() => selectAndNext({})} />
                    )}
                  </div>
                ))}
              </div>
            </StepShell>
          )}

          {currentKey === "tva_recup" && (
            <StepShell key="tva_recup" eyebrow="Véhicule d'occasion" title="TVA récupérable ?"
              subtitle="Le véhicule d'occasion (moins de 5 ans) a-t-il de la TVA récupérable ? Si oui, la TVA n'est pas finançable (crédit-bail et crédits MSA) : le montant financé est plafonné au prix HT et la TVA passe en apport.">
              <div className="grid gap-4">
                <OptionCard icon={CheckCircle2} testId="tva-recup-oui-card"
                  title="Oui, TVA récupérable" desc="Le montant à financer sera plafonné au prix HT, la TVA vient en apport."
                  selected={answers.tva_recuperable === true}
                  onClick={() => selectAndNext({ tva_recuperable: true })} />
                <OptionCard icon={XCircle} testId="tva-recup-non-card"
                  title="Non, pas de TVA récupérable" desc="Le montant à financer n'est pas modifié (financement TTC)."
                  selected={answers.tva_recuperable === false}
                  onClick={() => selectAndNext({ tva_recuperable: false })} />
              </div>
            </StepShell>
          )}


          {currentKey === "detention" && (() => {
            const isPro = answers.client_type === "pro" && answers.pro_anciennete_2ans === true;
            return (
            <StepShell key="detention" eyebrow="Projet" title="Durée de détention souhaitée"
              subtitle={`Comptez-vous garder votre remorque longtemps ou la renouveler régulièrement ? Cela oriente vers le crédit Sofinco ou le ${isPro ? "crédit-bail" : "la LOA Socram"}.`}>
              <div className="grid gap-4">
                <OptionCard icon={BadgeCheck} testId="detention-longtemps-card"
                  title="Garder longtemps" desc="J'ai le projet de garder la remorque dans le temps."
                  selected={answers.detention === "longtemps"}
                  onClick={() => selectAndNext({ detention: "longtemps" })} />
                <OptionCard icon={RotateCcw} testId="detention-renouveler-card"
                  title="Renouveler régulièrement" desc="Je veux pouvoir renouveler ma remorque régulièrement pour avoir du matériel récent et en bon état."
                  selected={answers.detention === "renouveler"}
                  onClick={() => selectAndNext({ detention: "renouveler" })} />
                <OptionCard icon={Sparkles} testId="detention-incertain-card"
                  title="Je choisis la liberté" desc="Je ne suis pas sûr de garder la remorque dans le temps et je ne veux pas m'embêter à la revendre."
                  selected={answers.detention === "incertain"}
                  onClick={() => selectAndNext({ detention: "incertain" })} />
              </div>
            </StepShell>
            );
          })()}


          {currentKey === "montant" && (
            <StepShell key="montant" eyebrow="Projet" title="Montant à financer"
              subtitle="Renseignez le prix et l'apport (le montant à financer se calcule), ou saisissez directement le montant. Seuil clé : 3 000 €.">
              <div className="space-y-3 max-w-lg">
                <div className="pb-2"><EtatVehiculeField answers={answers} set={set} /></div>
                <div>
                  <Label htmlFor="prix" className="text-sm font-semibold">Prix de la remorque TTC (€)</Label>
                  <Input id="prix" type="number" min="0" data-testid="prix-input"
                    placeholder="" value={answers.prix_remorque}
                    onChange={(e) => onPrixStep(e.target.value)}
                    className="mt-2 h-12 bg-white" />
                  <DevisPrixWarning prix={answers.prix_remorque} onRestore={() => onPrixStep(String(DEVIS_PRIX))} />
                </div>
                <div>
                  <Label htmlFor="apport" className={`text-sm font-semibold ${answers.apport_auto ? "text-red-600" : ""}`}>Apport (€) <span className="font-normal text-muted-foreground">(acompte + prix de la reprise s'il y a lieu)</span></Label>
                  <Input id="apport" type="number" min="0" data-testid="apport-input"
                    placeholder="" value={answers.apport_eur}
                    onChange={(e) => {
                      const ap = e.target.value;
                      const p = parseFloat(answers.prix_remorque);
                      let a = parseFloat(ap) || 0;
                      if (!Number.isNaN(p) && a > p) a = p; // l'apport ne peut dépasser le prix
                      const val = (!Number.isNaN(p) && (parseFloat(ap) || 0) > p) ? String(a) : ap;
                      set({ apport_eur: val, apport_auto: false, apport_touched: true, montant: !Number.isNaN(p) ? String(Math.max(p - a, 0)) : answers.montant });
                    }}
                    className={`mt-2 h-12 bg-white ${answers.apport_auto ? "text-red-600 font-bold border-red-400 focus-visible:ring-red-300" : ""}`} />
                </div>

                <div className="rounded-2xl bg-[#faf7f1] border border-[#e0d3ba] p-4">
                  <Label htmlFor="montant" className="text-sm font-semibold flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-[#9E7B41]" /> Montant à financer (€) *
                  </Label>
                  <Input id="montant" type="number" min="0" data-testid="montant-input"
                    placeholder="" value={answers.montant}
                    onChange={(e) => {
                      const mt = e.target.value;
                      const prix = parseFloat(answers.prix_remorque);
                      let m = parseFloat(mt);
                      if (!Number.isNaN(prix) && !Number.isNaN(m)) {
                        if (m > prix) m = prix; // le montant à financer ne peut dépasser le prix
                        set({ montant: String(m), apport_eur: String(Math.max(prix - m, 0)), apport_auto: false, apport_touched: true });
                      } else {
                        set({ montant: mt, apport_touched: true });
                      }
                    }}
                    className="mt-2 h-12 text-lg font-bold bg-white" />
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Valeur utilisée pour toutes les recommandations et simulations. Modifiable manuellement.
                  </p>
                </div>
              </div>

              <Button data-testid="montant-next-button" disabled={!answers.montant || !etatVehiculeOk(answers)}
                onClick={goNext}
                className="mt-6 bg-[#9E7B41] hover:bg-[#876634] h-12 px-7 rounded-xl font-semibold">
                Continuer <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </StepShell>
          )}

          {currentKey === "lead" && (
            <div key="lead" className="px-1 pb-10">
              <LeadForm
                simulationResume={`${profileLabel(answers)} · Montant à financer : ${answers.montant || "-"} €`}
                answers={answers}
                onSubmitted={(v) => {
                  setLeadVendeur(v);
                  setLeadSubmitted(true);
                  sessionStorage.setItem("fautras_lead_done", "1");
                  const ns = computeSteps(answers, false);
                  const ri = ns.indexOf("result");
                  setStepIndex(ri >= 0 ? ri : ns.length - 1);
                }}
              />
            </div>
          )}

          {currentKey === "result" && !result && (
            <div key="result-loading" className="max-w-3xl mx-auto py-24 text-center text-muted-foreground">
              <div className="animate-spin h-8 w-8 border-2 border-[#9E7B41] border-t-transparent rounded-full mx-auto mb-4" />
              Calcul de la recommandation…
            </div>
          )}

          {currentKey === "result" && result && (
            <div key="result">
              {leadVendeur && (
                <div data-testid="lead-vendeur-banner"
                  className="max-w-3xl mx-auto mb-6 rounded-2xl border border-[#e0d3ba] bg-[#faf7f1] p-4 flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-[#2b2b2b]">
                    Votre dossier a été transmis à <strong>{formatVendeurName(leadVendeur.vendeur)}</strong>
                    {leadVendeur.societe ? ` (${leadVendeur.societe})` : ""}. Il vous recontactera prochainement — un email de confirmation vient de vous être envoyé.
                  </p>
                </div>
              )}
              <ResultView answers={answers} result={result}
                onRestart={restart} onExcel={downloadExcel} onPdf={downloadPdf} onAdjustAmounts={set} />
            </div>
          )}

        </AnimatePresence>
      </main>
    </div>
  );
}

// ---------------- RESULT ----------------
const profileLabel = (a) => {
  const yr = parseInt(a.annee_naissance, 10);
  const ageStr = a.annee_naissance && yr ? ` · ${new Date().getFullYear() - yr} ans` : "";
  if (!a.client_type) return `Client${ageStr}`;
  if (a.client_type === "particulier") {
    const map = { cdi: "CDI", profession_liberale: "Profession libérale", gerant: "Gérant de société", retraite: "Retraité", auto_entrepreneur: "Auto-entrepreneur", association: "Association", pro_moins_2ans: "Pro – de 2 ans", cdd: "CDD", chomage: "Sans emploi" };
    return `Particulier · ${map[a.situation] || "—"}${ageStr}`;
  }
  let s = "Professionnel";
  if (a.pro_anciennete_2ans === false) {
    return `Professionnel -2 ans → Particulier${ageStr}`;
  }
  s += " · 2 ans ou +";
  if (a.affilie_msa) s += " · Affilié MSA";
  else s += a.lien_equestre ? " · Hors MSA · équestre" : " · Hors MSA";
  return s;
};

const eur = (n) =>
  (Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

// Montant TTC + équivalent HT entre parenthèses (TVA 20 %).
const eurTTC = (ttc) => `${eur(ttc)} TTC (${eur((Number(ttc) || 0) / 1.2)} HT)`;

// Mensualité + coût du crédit d'une solution à une durée donnée (miroir du backend). null si non calculable.
const nearestDuree = (durees, d) => durees.reduce((a, b) => (Math.abs(b - d) < Math.abs(a - d) ? b : a));
function solutionSim(r, montant, prix, apport, duree) {
  const C = Math.max(parseFloat(montant) || 0, 0);
  try {
    if (r.bareme_sofinco) {
      const b = r.bareme_sofinco;
      const n = Math.min(Math.max(parseInt(duree, 10), b.duree_min), b.duree_max);
      const rr = Math.pow(1 + b.taeg, 1 / 12) - 1;
      const pmt = rr === 0 ? C / n : (C * rr) / (1 - Math.pow(1 + rr, -n));
      const m = Math.round(pmt * 100) / 100;
      return { mensualite: m, duree: n, cout: Math.max(Math.round((m * n - C) * 100) / 100, 0) };
    }
    if (r.bareme_credit) {
      const b = r.bareme_credit;
      const n = nearestDuree(b.durees, parseInt(duree, 10));
      const taux = b.taux[String(n)];
      const rr = taux / 12;
      const pmt = rr === 0 ? C / n : (C * rr) / (1 - Math.pow(1 + rr, -n));
      const m = b.arrondi_euro_sup ? Math.ceil(pmt) : Math.round(pmt * 100) / 100;
      const frais = b.frais_dossier || 0;
      return { mensualite: m, duree: n, cout: Math.max(Math.round((m * n + frais - C) * 100) / 100, 0) };
    }
    if (r.bareme) {
      const b = r.bareme;
      const base = Math.max(parseFloat(prix) || C, 0);   // crédit-bail : coef sur le prix TTC
      const n = nearestDuree(b.durees, parseInt(duree, 10));
      const idx = base >= b.seuil_montant ? 1 : 0;
      const coeff = b.coeffs["0"][String(n)][idx];         // palier 1er loyer 0 % par défaut
      const mens = Math.round((base * coeff) / 100 * 100) / 100;
      const vr = Math.round((base * b.valeur_residuelle_pct) / 100 * 100) / 100;
      const frais = Math.round(b.frais_dossier_ht * (1 + b.tva) * 100) / 100;
      return { mensualite: mens, duree: n, cout: Math.max(Math.round((mens * n + vr + frais - base) * 100) / 100, 0) };
    }
    if (r.bareme_socram) {
      const b = r.bareme_socram;
      const n = nearestDuree(b.durees, parseInt(duree, 10));
      const E = Math.max(parseFloat(prix) || C, 0);
      const Cc = Math.max(parseFloat(apport) || 0, 0);
      const d = String(n);
      const rr = b.taux[d] / 12;
      const F = Math.round(E * b.vr_pct[d] * 100) / 100;
      const loyer = Cc === 0 ? excelPmt(rr, n, -E, F, 1) : excelPmt(rr, n - 1, -(E - Cc) * (1 + rr), F, 1);
      const premier = Cc > 0 ? Cc : loyer;
      const total = F + premier + (n - 1) * loyer;
      return { mensualite: Math.round(loyer * 100) / 100, duree: n, cout: Math.max(Math.round((total - E) * 100) / 100, 0) };
    }
  } catch (e) {
    return null;
  }
  return null;
}

const excelPmt = (rate, nper, pv, fv, type) => {
  if (rate === 0) return -(pv + fv) / nper;
  const p = Math.pow(1 + rate, nper);
  return -(pv * p + fv) / ((1 + rate * type) * ((p - 1) / rate));
};

function SocramLoaSimulator({ sol, bareme, initial, prixInit, apportVal, onApportChange, solId }) {
  const [prix, setPrix] = useState(Math.round(prixInit || initial) || 15000);
  const [duree, setDuree] = useState(48);
  const [avecAssurance, setAvecAssurance] = useState(false);

  const E = Math.max(parseFloat(prix) || 0, 0);
  const C = Math.max(parseFloat(apportVal) || 0, 0);
  const D = parseInt(duree, 10);
  const d = String(D);
  const g = bareme.taux[d];
  const r = g / 12;
  const F = E * bareme.vr_pct[d];
  const loyerBase = C === 0 ? excelPmt(r, D, -E, F, 1) : excelPmt(r, D - 1, -(E - C) * (1 + r), F, 1);
  const P = ((E - F) * bareme.assurance_taux_40_59) / 100 / 12;
  const premier = C > 0 ? C : (avecAssurance ? loyerBase + P : loyerBase);
  const suivant = avecAssurance ? loyerBase + P : loyerBase;
  const nb = D - 1;
  const totalHorsOA = (C > 0 ? C : loyerBase) + nb * loyerBase;
  const optionAchat = F;
  const totalAvecOA = optionAchat + totalHorsOA + (avecAssurance ? P * D : 0);

  return (
    <div className="border-t border-border bg-[#faf7f1] p-5 sm:p-6" data-testid={`socram-sim-${solId}`}>
      <p className="flex items-center gap-2 font-heading font-bold text-[#2b2b2b] mb-1">
        <Calculator className="h-5 w-5 text-[#9E7B41]" /> Simulateur de mensualité — LOA Socram
      </p>
      <p className="text-xs text-muted-foreground mb-4">
        Barème Socram · valeur résiduelle et taux selon la durée · estimation indicative
      </p>

      <div className="flex flex-col-reverse gap-5">
        <div className="space-y-4">
          <div>
            <Label htmlFor={`socram-prix-${solId}`} className="text-sm font-semibold">Prix du véhicule (€)</Label>
            <Input id={`socram-prix-${solId}`} type="number" min="0" data-testid={`socram-prix-${solId}`}
              value={prix} onChange={(e) => setPrix(e.target.value)} className="mt-2 h-12 bg-white" />
          </div>

          <div>
            <Label className="text-sm font-semibold">Durée</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {bareme.durees.map((m) => (
                <button key={m} type="button" data-testid={`socram-duree-${m}-${solId}`}
                  onClick={() => setDuree(m)}
                  className={`rounded-lg border-2 py-2 text-sm font-semibold transition-colors ${
                    D === m ? "border-[#9E7B41] bg-white text-[#2b2b2b]" : "border-border bg-white/60 text-muted-foreground"
                  }`}>
                  {m} mois
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Taux : <span className="font-semibold text-[#9E7B41]">{(g * 100).toFixed(2)}%</span> ·
              {" "}VR : <span className="font-semibold text-[#9E7B41]">{(bareme.vr_pct[d] * 100).toFixed(2)}%</span>
            </p>
          </div>

          <div>
            <Label htmlFor={`socram-apport-${solId}`} className="text-sm font-semibold">Apport en 1<sup>er</sup> loyer (€) <span className="font-normal text-muted-foreground">= apport du haut</span></Label>
            <Input id={`socram-apport-${solId}`} type="number" min="0" data-testid={`socram-apport-${solId}`}
              value={apportVal} onChange={(e) => onApportChange(e.target.value)} className="mt-2 h-12 bg-white" />
          </div>

          <div>
            <Label className="text-sm font-semibold">Assurance emprunteur</Label>
            <div className="mt-2 grid grid-cols-1 gap-2">
              <button type="button" data-testid={`socram-assurance-sans-${solId}`} onClick={() => setAvecAssurance(false)}
                className={`rounded-xl border-2 px-4 py-3 text-left text-sm font-medium transition-colors ${
                  !avecAssurance ? "border-[#9E7B41] bg-white text-[#2b2b2b]" : "border-border bg-white/60 text-muted-foreground"
                }`}>
                Sans assurance emprunteur
              </button>
              <button type="button" data-testid={`socram-assurance-avec-${solId}`} onClick={() => setAvecAssurance(true)}
                className={`rounded-xl border-2 px-4 py-3 text-left text-sm font-medium transition-colors ${
                  avecAssurance ? "border-[#9E7B41] bg-white text-[#2b2b2b]" : "border-border bg-white/60 text-muted-foreground"
                }`}>
                Avec assurance emprunteur
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-[#434343] text-white p-5">
          <p className="text-xs uppercase tracking-wider text-white/60">Loyers suivants</p>
          <p className="font-heading text-4xl font-extrabold mt-1" data-testid={`socram-loyer-${solId}`}>{eur(suivant)}</p>
          <p className="text-xs text-white/50 mt-1">× {nb} loyers · {D} mois</p>

          <div className="h-px bg-white/15 my-4" />
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-white/70">1<sup>er</sup> loyer{C > 0 ? " (apport)" : ""}</dt>
              <dd className="font-semibold" data-testid={`socram-premier-${solId}`}>{eur(premier)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-white/70">Option d'achat (VR)</dt>
              <dd className="font-semibold" data-testid={`socram-oa-${solId}`}>{eur(optionAchat)}</dd>
            </div>
            {avecAssurance && (
              <div className="flex justify-between">
                <dt className="text-white/70">Assurance / mois</dt>
                <dd className="font-semibold">{eur(P)}</dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-white/70">Coût du crédit</dt>
              <dd className="font-semibold" data-testid={`socram-cout-${solId}`}>{eur(Math.max(totalAvecOA - E, 0))}</dd>
            </div>
            <div className="flex justify-between pt-2 border-t border-white/15">
              <dt className="text-white/70">Total dû avec option d'achat</dt>
              <dd className="font-bold text-[#e0c79a]" data-testid={`socram-total-${solId}`}>{eur(totalAvecOA)}</dd>
            </div>
          </dl>
          <ApplyDevisButton sol={sol} solId={solId} duree={D} mensualite={suivant} premier={premier} taeg={null} montant={E} prix_ttc={E} cout_total={Math.max(totalAvecOA - E, 0)} />
        </div>
      </div>
    </div>
  );
}


function AmortSimulator({ sol, bareme, initial, solId }) {
  // Le montant à financer correspond à la case « à financer » du haut (HT si TVA récupérable,
  // sinon le montant demandé par le client). Aucun plafonnement interne ici.
  const [montant, setMontant] = useState(Math.round(initial) || 10000);
  const [duree, setDuree] = useState(48);

  const C = Math.max(parseFloat(montant) || 0, 0);
  const n = parseInt(duree, 10);
  const taux = bareme.taux[String(n)] ?? bareme.taux[String(bareme.durees[0])];
  const r = taux / 12;
  const pmtRaw = r === 0 ? C / n : (C * r) / (1 - Math.pow(1 + r, -n));
  const mensualite = bareme.arrondi_euro_sup ? Math.ceil(pmtRaw) : Math.round(pmtRaw * 100) / 100;
  const frais = bareme.frais_dossier || 0;
  const total = mensualite * n + frais;

  return (
    <div className="border-t border-border bg-[#faf7f1] p-5 sm:p-6" data-testid={`amort-sim-${solId}`}>
      <p className="flex items-center gap-2 font-heading font-bold text-[#2b2b2b] mb-1">
        <Calculator className="h-5 w-5 text-[#9E7B41]" /> Simulateur de mensualité — {bareme.provider}
      </p>
      <p className="text-xs text-muted-foreground mb-4">
        Crédit amortissable · taux {(taux * 100).toFixed(2).replace(".", ",")} % · frais de dossier {frais} € · estimation indicative
      </p>

      <div className="flex flex-col-reverse gap-5">
        <div className="space-y-4">
          <div>
            <Label htmlFor={`amort-montant-${solId}`} className="text-sm font-semibold">Montant à financer (€)</Label>
            <Input id={`amort-montant-${solId}`} type="number" min="0" data-testid={`amort-montant-${solId}`}
              value={montant} onChange={(e) => setMontant(e.target.value)} className="mt-2 h-12 bg-white" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Durée</Label>
            <div className="mt-2 grid grid-cols-5 gap-2">
              {bareme.durees.map((mo) => (
                <button key={mo} type="button" data-testid={`amort-duree-${mo}-${solId}`}
                  onClick={() => setDuree(mo)}
                  className={`rounded-lg border-2 py-2 text-xs font-semibold transition-colors ${
                    n === mo ? "border-[#9E7B41] bg-white text-[#2b2b2b]" : "border-border bg-white/60 text-muted-foreground"
                  }`}>
                  {mo} m
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-[#434343] text-white p-5">
          <p className="text-xs uppercase tracking-wider text-white/60">Mensualité estimée</p>
          <p className="font-heading text-4xl font-extrabold mt-1" data-testid={`amort-mensualite-${solId}`}>{eur(mensualite)}</p>
          <p className="text-xs text-white/50 mt-1">× {n} mois · taux {(taux * 100).toFixed(2).replace(".", ",")} %</p>
          <div className="h-px bg-white/15 my-4" />
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-white/70">Frais de dossier</dt>
              <dd className="font-semibold">{eur(frais)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-white/70">Coût du crédit</dt>
              <dd className="font-semibold text-right" data-testid={`amort-cout-${solId}`}>{eurTTC(Math.max(total - C, 0))}</dd>
            </div>
            <div className="flex justify-between pt-2 border-t border-white/15">
              <dt className="text-white/70">Total (échéances + frais)</dt>
              <dd className="font-bold text-[#e0c79a]" data-testid={`amort-total-${solId}`}>{eur(total)}</dd>
            </div>
          </dl>
          <ApplyDevisButton sol={sol} solId={solId} duree={n} mensualite={mensualite}
            taeg={taegFrom(C - frais, Array(n).fill(mensualite))} montant={C} cout_total={Math.max(total - C, 0)} />
        </div>
      </div>
    </div>
  );
}

function SofincoCreditSimulator({ sol, bareme, initial, solId, pro }) {
  const [montant, setMontant] = useState(Math.round(initial) || 10000);
  const [duree, setDuree] = useState(60);

  const C = Math.max(parseFloat(montant) || 0, 0);
  const n = parseInt(duree, 10);
  const r = Math.pow(1 + bareme.taeg, 1 / 12) - 1; // taux mensuel équivalent au TAEG
  const pmtRaw = r === 0 ? C / n : (C * r) / (1 - Math.pow(1 + r, -n));
  const mensualite = Math.round(pmtRaw * 100) / 100;
  const totalDu = Math.round(mensualite * n * 100) / 100;
  const coutCredit = Math.max(totalDu - C, 0);

  return (
    <div className="border-t border-border bg-[#faf7f1] p-5 sm:p-6" data-testid={`sofinco-sim-${solId}`}>
      <p className="flex items-center gap-2 font-heading font-bold text-[#2b2b2b] mb-1">
        <Calculator className="h-5 w-5 text-[#9E7B41]" /> Choisissez vos mensualités — Sofinco
      </p>
      <p className="text-xs text-muted-foreground mb-4">
        Crédit classique · TAEG fixe {(bareme.taeg * 100).toFixed(2).replace(".", ",")} % · hors assurance facultative · estimation indicative
      </p>

      <div className="flex flex-col-reverse gap-5">
        <div className="space-y-5">
          <div>
            <Label htmlFor={`sofinco-montant-${solId}`} className="text-sm font-semibold">Montant à financer (€)</Label>
            <Input id={`sofinco-montant-${solId}`} type="number" min="0" data-testid={`sofinco-montant-${solId}`}
              value={montant} onChange={(e) => setMontant(e.target.value)} className="mt-2 h-12 bg-white" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Durée du crédit</Label>
              <span className="text-sm font-bold text-white bg-[#9E7B41] rounded-full px-3 py-0.5" data-testid={`sofinco-duree-val-${solId}`}>
                {n} mois
              </span>
            </div>
            <Slider min={bareme.duree_min} max={bareme.duree_max} step={1} value={[n]}
              onValueChange={(v) => setDuree(v[0])} data-testid={`sofinco-slider-${solId}`}
              className="mt-2 [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-[#9E7B41]" />
            <div className="flex justify-between text-[11px] text-muted-foreground mt-1.5">
              <span>{bareme.duree_min} mois</span>
              <span>{bareme.duree_max} mois</span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-[#434343] text-white p-5">
          <p className="text-xs uppercase tracking-wider text-white/60">{n} mensualités de</p>
          <p className="font-heading text-4xl font-extrabold mt-1" data-testid={`sofinco-mensualite-${solId}`}>{eur(mensualite)}</p>
          <p className="text-xs text-white/50 mt-1">sur {n} mois · TAEG fixe {(bareme.taeg * 100).toFixed(2).replace(".", ",")} %</p>
          <div className="h-px bg-white/15 my-4" />
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-white/70">Montant emprunté</dt>
              <dd className="font-semibold">{eur(C)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-white/70">Coût du crédit</dt>
              <dd className="font-semibold text-right" data-testid={`sofinco-cout-${solId}`}>{pro ? eurTTC(coutCredit) : eur(coutCredit)}</dd>
            </div>
            <div className="flex justify-between pt-2 border-t border-white/15">
              <dt className="text-white/70">Montant total dû</dt>
              <dd className="font-bold text-[#e0c79a]" data-testid={`sofinco-total-${solId}`}>{eur(totalDu)}</dd>
            </div>
          </dl>
          <ApplyDevisButton sol={sol} solId={solId} duree={n} mensualite={mensualite} taeg={bareme.taeg} montant={C} cout_total={coutCredit} />
          <p className="text-[10px] text-white/40 mt-3 leading-relaxed">
            Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager.
          </p>
        </div>
      </div>
    </div>
  );
}

function CreditBailSimulator({ sol, bareme, prixInit, solId }) {
  // Mutualease finance le TTC : le coefficient s'applique au prix TTC du véhicule.
  // 1er loyer au choix (0/10/20/35%) ; s'il est > 0, il occupe la 1re échéance (→ durée-1 loyers).
  const base = prixInit > 0 ? prixInit : 0;
  const [duree, setDuree] = useState(60);
  const [palier, setPalier] = useState(0);
  const [ass, setAss] = useState([]);

  const toggleAss = (id) =>
    setAss((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const idx = base >= bareme.seuil_montant ? 1 : 0;
  const coeff = bareme.coeffs[String(palier)][String(duree)][idx];
  const loyer = (base * coeff) / 100;
  const premierLoyer = (palier / 100) * base;
  const nbLoyers = palier === 0 ? duree : duree - 1;
  const vr = (base * bareme.valeur_residuelle_pct) / 100;
  const fraisTTC = Math.round(bareme.frais_dossier_ht * (1 + bareme.tva) * 100) / 100;
  const tauxAss = bareme.assurances.filter((a) => ass.includes(a.id)).reduce((s, a) => s + a.taux_mensuel_pct, 0);
  const assMens = (tauxAss / 100) * base;
  const loyerTotal = loyer + assMens;
  const total = premierLoyer + nbLoyers * loyer + assMens * duree + vr + fraisTTC;
  const cout = Math.max(total - base, 0);
  // Équivalents HT (TVA sur loyers, valeur résiduelle et frais ; l'assurance n'est pas soumise à TVA).
  const ht = (v) => v / (1 + bareme.tva);
  const totalHT = ht(premierLoyer + nbLoyers * loyer + vr + fraisTTC) + assMens * duree;
  const coutHT = Math.max(totalHT - ht(base), 0);
  const htTag = (v, id) => <span className="font-normal text-white/60" data-testid={id}> ({eur(v)} HT)</span>;

  return (
    <div className="border-t border-border bg-[#faf7f1] p-5 sm:p-6" data-testid={`simulator-${solId}`}>
      <p className="flex items-center gap-2 font-heading font-bold text-[#2b2b2b] mb-1">
        <Calculator className="h-5 w-5 text-[#9E7B41]" /> Simulateur de mensualité — Crédit-bail
      </p>
      <p className="text-xs text-muted-foreground mb-4">
        Barème Mutualease · financement du prix <strong>TTC</strong> · valeur résiduelle {bareme.valeur_residuelle_pct}% · frais de dossier {eur(fraisTTC)} TTC
      </p>

      {base <= 0 ? (
        <p className="text-sm text-muted-foreground" data-testid={`sim-noprice-${solId}`}>
          Renseignez le prix TTC du véhicule (bandeau du haut) pour estimer le loyer.
        </p>
      ) : (
        <div className="flex flex-col-reverse gap-5">
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground" data-testid={`sim-base-${solId}`}>
              Prix financé : <span className="font-semibold text-[#9E7B41]">{eur(base)} TTC</span> ({eur(ht(base))} HT) · barème {base >= bareme.seuil_montant ? "≥ 10 000 €" : "< 10 000 €"}.
            </p>

            <div>
              <Label className="text-sm font-semibold">1er loyer majoré</Label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {bareme.premiers_loyers.map((p) => (
                  <button key={p} type="button" data-testid={`sim-palier-${p}-${solId}`}
                    onClick={() => setPalier(p)}
                    className={`rounded-lg border-2 py-2 text-sm font-semibold transition-colors ${
                      palier === p ? "border-[#9E7B41] bg-white text-[#2b2b2b]" : "border-border bg-white/60 text-muted-foreground"
                    }`}>
                    {p} %
                  </button>
                ))}
              </div>
              {palier > 0 && (
                <p className="text-xs text-muted-foreground mt-2" data-testid={`sim-premier-note-${solId}`}>
                  1er loyer : <span className="font-semibold text-[#9E7B41]">{eur(premierLoyer)} TTC</span> ({eur(ht(premierLoyer))} HT) (payé au départ, tient lieu d'apport) · puis {nbLoyers} loyers.
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm font-semibold">Durée</Label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {bareme.durees.map((mo) => (
                  <button key={mo} type="button" data-testid={`sim-duree-${mo}-${solId}`}
                    onClick={() => setDuree(mo)}
                    className={`rounded-lg border-2 py-2 text-sm font-semibold transition-colors ${
                      duree === mo ? "border-[#9E7B41] bg-white text-[#2b2b2b]" : "border-border bg-white/60 text-muted-foreground"
                    }`}>
                    {mo} mois
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Coefficient : <span className="font-semibold text-[#9E7B41]">{coeff}%</span>
              </p>
            </div>

            <div>
              <Label className="text-sm font-semibold">Assurance (facultative)</Label>
              <div className="mt-2 space-y-2">
                {bareme.assurances.map((a) => (
                  <label key={a.id} data-testid={`sim-ass-${a.id}-${solId}`}
                    className="flex items-center gap-3 rounded-lg border border-border bg-white/70 px-3 py-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={ass.includes(a.id)} onChange={() => toggleAss(a.id)}
                      className="h-4 w-4 accent-[#9E7B41]" />
                    <span className="flex-1">{a.label}</span>
                    <span className="text-muted-foreground">{String(a.taux_mensuel_pct).replace(".", ",")} %/mois</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-[#434343] text-white p-5">
            <p className="text-xs uppercase tracking-wider text-white/60">Loyer mensuel estimé</p>
            <p className="font-heading text-4xl font-extrabold mt-1" data-testid={`sim-mensualite-${solId}`}>
              {eur(loyerTotal)} <span className="text-lg font-bold align-middle">TTC</span>
            </p>
            <p className="text-sm font-semibold text-white/80" data-testid={`sim-mensualite-ht-${solId}`}>({eur(ht(loyer) + assMens)} HT)</p>
            <p className="text-xs text-white/50 mt-1">
              sur {duree} mois · coeff {coeff}%{assMens > 0 ? ` · dont assurance ${eur(assMens)}` : ""}
            </p>

            <div className="h-px bg-white/15 my-4" />
            <dl className="space-y-2 text-sm">
              {palier > 0 && (
                <div className="flex justify-between gap-3">
                  <dt className="text-white/70">1er loyer majoré ({palier}%)</dt>
                  <dd className="font-semibold text-right" data-testid={`sim-premier-loyer-${solId}`}>{eur(premierLoyer)}{htTag(ht(premierLoyer), `sim-premier-loyer-ht-${solId}`)}</dd>
                </div>
              )}
              <div className="flex justify-between gap-3">
                <dt className="text-white/70">Valeur résiduelle ({bareme.valeur_residuelle_pct}%)</dt>
                <dd className="font-semibold text-right">{eur(vr)}{htTag(ht(vr), `sim-vr-ht-${solId}`)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-white/70">Frais de dossier</dt>
                <dd className="font-semibold text-right">{eur(fraisTTC)}{htTag(bareme.frais_dossier_ht, `sim-frais-ht-${solId}`)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-white/70">Coût du crédit</dt>
                <dd className="font-semibold text-right" data-testid={`sim-cout-${solId}`}>{eur(cout)}{htTag(coutHT, `sim-cout-ht-${solId}`)}</dd>
              </div>
              <div className="flex justify-between gap-3 pt-2 border-t border-white/15">
                <dt className="text-white/70">Coût total estimé</dt>
                <dd className="font-bold text-[#e0c79a] text-right" data-testid={`sim-total-${solId}`}>{eur(total)}{htTag(totalHT, `sim-total-ht-${solId}`)}</dd>
              </div>
            </dl>
            <ApplyDevisButton sol={sol} solId={solId} duree={duree} mensualite={loyerTotal} premier={palier > 0 ? premierLoyer + assMens : null} taeg={null} montant={base} prix_ttc={base} cout_total={cout} />
          </div>
        </div>
      )}
    </div>
  );
}

function FractionneSimulator({ sol, bareme, initial, solId }) {
  // Paiement Sofinco en 2 / 3 / 4 fois (≤ plafond). Le coût du crédit (frais) est
  // intégralement payé par le client sur la 1re mensualité.
  const plafond = bareme.plafond || 3000;
  const [montant, setMontant] = useState(Math.min(Math.round(initial) || 3000, plafond));
  const [nb, setNb] = useState(3);

  const onMontant = (v) => {
    let num = parseFloat(v);
    if (Number.isNaN(num)) { setMontant(v); return; }
    if (num > plafond) num = plafond;
    if (num < 0) num = 0;
    setMontant(num);
  };

  const C = Math.max(parseFloat(montant) || 0, 0);
  const taux = bareme.modalites[String(nb)];
  const frais = Math.round(C * taux * 100) / 100;      // coût du crédit
  const base = Math.round((C / nb) * 100) / 100;        // part de capital par versement
  const premier = Math.round((base + frais) * 100) / 100; // 1er versement = part + TOTALITÉ des frais
  const total = Math.round((C + frais) * 100) / 100;

  const moisFr = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  const echeances = Array.from({ length: nb }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() + i);
    const label = i === 0 ? "Aujourd'hui" : `${d.getDate()} ${moisFr[d.getMonth()]} ${d.getFullYear()}`;
    const montantVers = i === 0 ? premier : base;
    return { label, montantVers, frais: i === 0 ? frais : 0 };
  });

  return (
    <div className="border-t border-border bg-[#faf7f1] p-5 sm:p-6" data-testid={`fractionne-sim-${solId}`}>
      <p className="flex items-center gap-2 font-heading font-bold text-[#2b2b2b] mb-1">
        <Calculator className="h-5 w-5 text-[#9E7B41]" /> Simulateur — Paiement en 2, 3 ou 4 fois
      </p>
      <p className="text-xs text-muted-foreground mb-4">
        Sofinco · montant ≤ {plafond.toLocaleString("fr-FR")} € · le coût du crédit est réglé en totalité sur le 1<sup>er</sup> versement
      </p>

      <div className="flex flex-col-reverse gap-5">
        <div className="space-y-4">
          <div>
            <Label htmlFor={`fractionne-montant-${solId}`} className="text-sm font-semibold">Montant à financer (€)</Label>
            <Input id={`fractionne-montant-${solId}`} type="number" min="0" max={plafond} data-testid={`fractionne-montant-${solId}`}
              value={montant} onChange={(e) => onMontant(e.target.value)} className="mt-2 h-12 bg-white" />
            <p className="text-xs text-muted-foreground mt-1.5">Plafond du paiement fractionné : {plafond.toLocaleString("fr-FR")} €.</p>
          </div>

          <div>
            <Label className="text-sm font-semibold">Nombre de mensualités</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {["2", "3", "4"].map((m) => (
                <button key={m} type="button" data-testid={`fractionne-nb-${m}-${solId}`}
                  onClick={() => setNb(parseInt(m, 10))}
                  className={`rounded-lg border-2 py-2 text-sm font-semibold transition-colors ${
                    nb === parseInt(m, 10) ? "border-[#9E7B41] bg-white text-[#2b2b2b]" : "border-border bg-white/60 text-muted-foreground"
                  }`}>
                  {m}x
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Taux : <span className="font-semibold text-[#9E7B41]">{(taux * 100).toFixed(2).replace(".", ",")} %</span>
            </p>
          </div>

          <div className="rounded-xl border border-[#e0d3ba] bg-white overflow-hidden" data-testid={`fractionne-echeancier-${solId}`}>
            <div className="px-4 py-2 bg-[#faf7f1] border-b border-border text-xs font-semibold uppercase tracking-wide text-muted-foreground flex justify-between">
              <span>Échéancier</span><span>Dont frais</span>
            </div>
            {echeances.map((e, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm border-b border-border last:border-0">
                <span className="text-[#2b2b2b]">{e.label}</span>
                <span className="flex items-center gap-4">
                  <span className="font-bold text-[#2b2b2b]" data-testid={`fractionne-vers-${i}-${solId}`}>{eur(e.montantVers)}</span>
                  <span className="text-[#9E7B41] font-medium w-20 text-right">{e.frais > 0 ? eur(e.frais) : "—"}</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-[#434343] text-white p-5">
          <p className="text-xs uppercase tracking-wider text-white/60">1<sup>er</sup> versement (aujourd'hui)</p>
          <p className="font-heading text-4xl font-extrabold mt-1" data-testid={`fractionne-premier-${solId}`}>{eur(premier)}</p>
          <p className="text-xs text-white/50 mt-1">
            dont {eur(frais)} de coût du crédit{nb > 1 ? ` · puis ${nb - 1} × ${eur(base)}` : ""}
          </p>
          <div className="h-px bg-white/15 my-4" />
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-white/70">Montant financé</dt>
              <dd className="font-semibold">{eur(C)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-white/70">Coût du crédit ({(taux * 100).toFixed(2).replace(".", ",")} %)</dt>
              <dd className="font-semibold" data-testid={`fractionne-cout-${solId}`}>{eur(frais)}</dd>
            </div>
            <div className="flex justify-between pt-2 border-t border-white/15">
              <dt className="text-white/70">Montant total dû</dt>
              <dd className="font-bold text-[#e0c79a]" data-testid={`fractionne-total-${solId}`}>{eur(total)}</dd>
            </div>
          </dl>
          <ApplyDevisButton sol={sol} solId={solId} duree={nb} mensualite={base} premier={premier}
            taeg={taegFrom(C - premier, Array(nb - 1).fill(base))} montant={C} cout_total={frais} />
          <p className="text-[10px] text-white/40 mt-3 leading-relaxed">
            Le coût du crédit est réglé intégralement sur le 1<sup>er</sup> versement.
          </p>
        </div>
      </div>
    </div>
  );
}


function ResultView({ answers, result, onRestart, onExcel, onPdf, onAdjustAmounts }) {
  const { warnings = [], recommendations = [], blocking } = result;
  const montant = parseFloat(answers.montant || 0);
  const initialMontant = montant > 0 ? montant : 10000;
  const [compareDuree, setCompareDuree] = useState(60);
  const prixNum = parseFloat(answers.prix_remorque) || null;
  const apportNum = parseFloat(answers.apport_eur) || 0;
  const compareRows = recommendations
    .map((r) => ({ r, sim: solutionSim(r, montant, prixNum, apportNum, compareDuree) }))
    .filter((x) => x.sim);

  // Corrections automatiques du montant à financer (répercutées dans les cases du haut, apport en rouge/gras) :
  //  1) TVA non finançable pour les crédits bancaires MSA (AGILOR / Crédit Mutuel / Banque Populaire)
  //     quand la TVA est récupérable (véhicule neuf, ou occasion < 5 ans avec TVA récupérable)
  //     → montant plafonné au prix HT, TVA en apport. (Le crédit-bail Mutualease, lui, finance le TTC.)
  //  2) Paiement Sofinco 2/3/4 fois (CDD / sans emploi) plafonné à 3 000 € → apport = complément.
  const hasMsaCredit = recommendations.some((r) => r.bareme_credit);
  const tvaApplicable =
    answers.client_type === "pro" &&
    (answers.etat_vehicule === "neuf" ||
      (answers.etat_vehicule === "occasion" && answers.vehicule_moins_5ans === true && answers.tva_recuperable === true));
  const situationBloquee = answers.situation === "cdd" || answers.situation === "chomage";

  // Le crédit-bail serait-il la solution PRINCIPALE proposée (1re) ?
  // (hors-MSA équestre, ou MSA sans crédit bancaire disponible + volonté de renouveler)
  const vehEligible = answers.etat_vehicule === "neuf" ||
    (answers.etat_vehicule === "occasion" && answers.vehicule_moins_5ans === true);
  const banksList = ["credit_mutuel", "credit_agricole", "banque_populaire"];
  const bankValid = banksList.includes(answers.banque) && vehEligible;
  const creditBailPrimary =
    answers.client_type === "pro" && answers.pro_anciennete_2ans === true && vehEligible &&
    parseFloat(answers.montant || 0) > 3000 &&
    (
      (answers.affilie_msa === false && answers.lien_equestre === true) ||
      (answers.affilie_msa === true && !bankValid && (answers.detention === "renouveler" || answers.detention === "incertain"))
    );

  useEffect(() => {
    if (!onAdjustAmounts) return;
    const montantCur = parseFloat(answers.montant) || 0;
    const prix = parseFloat(answers.prix_remorque);

    // Crédit-bail principal + apport > 35 % : on ramène l'apport à 35 % (financé = 65 % du prix).
    if (creditBailPrimary && !Number.isNaN(prix) && prix > 0) {
      const min65 = Math.round(prix * 0.65);
      if (montantCur < min65) {
        onAdjustAmounts({ montant: String(min65), apport_eur: String(Math.max(prix - min65, 0)), apport_auto: true });
        return;
      }
    }

    let cap = null;
    if (hasMsaCredit && tvaApplicable && !Number.isNaN(prix) && prix > 0) {
      cap = Math.round(prix / 1.20);
    } else if (situationBloquee && montantCur > 3000) {
      cap = 3000;
    }
    if (cap != null && montantCur > cap) {
      const apportCur = parseFloat(answers.apport_eur) || 0;
      onAdjustAmounts({
        montant: String(cap),
        apport_eur: String(apportCur + (montantCur - cap)),
        apport_auto: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMsaCredit, tvaApplicable, situationBloquee, creditBailPrimary, answers.prix_remorque, answers.montant]);

  // Modifier l'apport depuis un simulateur (LOA) répercute dans le bandeau du haut (et vice versa).
  const setApportFromSim = (v) => {
    const p = parseFloat(answers.prix_remorque);
    let a = parseFloat(v) || 0;
    if (!Number.isNaN(p) && a > p) a = p;
    const val = (!Number.isNaN(p) && (parseFloat(v) || 0) > p) ? String(a) : v;
    onAdjustAmounts({ apport_eur: val, apport_auto: false, apport_touched: true, montant: !Number.isNaN(p) ? String(Math.max(p - a, 0)) : answers.montant });
  };

  // Modifier le montant à financer depuis un simulateur (crédit-bail) répercute dans le bandeau du haut.
  const setMontantFromSim = (v) => {
    const prix = parseFloat(answers.prix_remorque);
    let m = parseFloat(v);
    if (!Number.isNaN(prix) && !Number.isNaN(m)) {
      if (m > prix) m = prix;
      onAdjustAmounts({ montant: String(m), apport_eur: String(Math.max(prix - m, 0)), apport_auto: false, apport_touched: true });
    } else {
      onAdjustAmounts({ montant: v, apport_touched: true });
    }
  };
  void setMontantFromSim;


  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 print:block">
        <div>
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#9E7B41] mb-2">Recommandation</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#2b2b2b] tracking-tight">
            Solutions de financement
          </h2>
          <div className="flex flex-wrap gap-2 mt-3">
            <Badge className="bg-[#434343] hover:bg-[#434343] text-white" data-testid="result-profile-badge">
              {profileLabel(answers)}
            </Badge>
            {montant > 0 && (
              <Badge variant="outline" className="border-[#9E7B41] text-[#9E7B41]">
                <Wallet className="h-3 w-3 mr-1" /> {montant.toLocaleString("fr-FR")} €
              </Badge>
            )}
            {answers.etat_vehicule && (
              <Badge variant="outline" className="border-border">
                {answers.etat_vehicule === "neuf" ? "Neuf" : "Occasion"}
                {answers.etat_vehicule === "occasion" && (answers.vehicule_moins_5ans === true ? " (< 5 ans)" : answers.vehicule_moins_5ans === false ? " (≥ 5 ans)" : "")}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 print:hidden">
          <Button variant="outline" data-testid="print-button" onClick={() => window.print()}
            className="border-border">
            <Printer className="h-4 w-4 mr-2" /> Imprimer
          </Button>
          <Button variant="outline" data-testid="email-button" onClick={() => {
            const lignes = [];
            lignes.push("Fautras - Solutions de financement");
            lignes.push("");
            lignes.push("Profil : " + profileLabel(answers));
            if (prixNum) lignes.push("Prix de la remorque TTC : " + prixNum.toLocaleString("fr-FR") + " EUR");
            if (apportNum) lignes.push("Apport : " + apportNum.toLocaleString("fr-FR") + " EUR");
            if (montant > 0) lignes.push("Montant à financer : " + montant.toLocaleString("fr-FR") + " EUR");
            lignes.push("");
            recommendations.forEach((r, i) => {
              const sim = solutionSim(r, montant, prixNum, apportNum, compareDuree);
              lignes.push(`${i + 1}. ${r.name} (${r.provider})`);
              if (sim) lignes.push(`   ${sim.mensualite.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} EUR/mois sur ${sim.duree} mois · coût du crédit : ${sim.cout.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} EUR`);
              if (r.docs && r.docs.length) lignes.push("   Pièces à préparer : " + r.docs.join(", "));
              lignes.push("");
            });
            if (recommendations.length === 0) lignes.push("Aucune solution de financement pour ce profil.");
            (warnings || []).forEach((w) => lignes.push("⚠ " + w.message));
            const subject = encodeURIComponent("Fautras - Solutions de financement");
            const body = encodeURIComponent(lignes.join("\n"));
            window.location.href = `mailto:?subject=${subject}&body=${body}`;
          }} className="border-border">
            <Mail className="h-4 w-4 mr-2" /> Envoyer par email
          </Button>
          <Button variant="outline" data-testid="export-pdf-button" onClick={() => onPdf(compareDuree)} className="border-border">
            <Download className="h-4 w-4 mr-2" /> Fiche PDF
          </Button>
          <Button variant="outline" data-testid="export-excel-button" onClick={() => onExcel(compareDuree)} className="border-[#9E7B41] text-[#9E7B41] hover:bg-[#f3ede2]">
            <Download className="h-4 w-4 mr-2" /> Excel
          </Button>
        </div>
      </div>

      <div className="divider-gold my-6" />

      {compareRows.length >= 2 && (
        <div className="mb-8 rounded-2xl border border-[#e0d3ba] bg-white overflow-hidden" data-testid="comparateur">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-5 bg-[#faf7f1] border-b border-border">
            <p className="flex items-center gap-2 font-heading font-bold text-[#2b2b2b]">
              <Calculator className="h-5 w-5 text-[#9E7B41]" /> Comparateur des mensualités
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Durée :</span>
              {[48, 60, 72].map((mo) => (
                <button key={mo} type="button" data-testid={`compare-duree-${mo}`}
                  onClick={() => setCompareDuree(mo)}
                  className={`rounded-lg border-2 px-3 py-1.5 text-xs font-semibold transition-colors ${
                    compareDuree === mo ? "border-[#9E7B41] bg-white text-[#2b2b2b]" : "border-border bg-white/60 text-muted-foreground"
                  }`}>
                  {mo} m
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-white">
                  <th className="px-4 py-2 font-semibold">Solution</th>
                  <th className="px-4 py-2 font-semibold">Durée</th>
                  <th className="px-4 py-2 font-semibold text-right">Mensualité</th>
                  <th className="px-4 py-2 font-semibold text-right">{answers.client_type === "pro" ? "Coût du crédit HT" : "Coût du crédit"}</th>
                </tr>
              </thead>
              <tbody>
                {compareRows
                  .slice()
                  .sort((a, b) => a.sim.mensualite - b.sim.mensualite)
                  .map(({ r, sim }, i) => (
                    <tr key={r.id} data-testid={`compare-row-${r.id}`}
                      className={`border-t border-border ${i === 0 ? "bg-[#f3ede2]" : ""}`}>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-[#2b2b2b]">{r.provider}</span>
                        <span className="text-muted-foreground"> · {r.name}</span>
                        {i === 0 && <Badge className="ml-2 bg-[#9E7B41] hover:bg-[#9E7B41] text-white text-[10px]">Mensualité la plus basse</Badge>}
                      </td>
                      <td className="px-4 py-3">{sim.duree} mois</td>
                      <td className="px-4 py-3 text-right font-bold text-[#2b2b2b]" data-testid={`compare-mensualite-${r.id}`}>{eur(sim.mensualite)}</td>
                      <td className="px-4 py-3 text-right text-[#9E7B41] font-semibold" data-testid={`compare-cout-${r.id}`}>{eur(answers.client_type === "pro" ? sim.cout / 1.2 : sim.cout)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-2 text-[11px] text-muted-foreground bg-[#faf7f1] border-t border-border">
            Estimations indicatives à {compareDuree} mois, hors assurance facultative. Montant à financer : {eur(montant)}.
          </p>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="space-y-3 mb-8" data-testid="warnings-block">
          {warnings.map((w, i) => {
            const isBlock = w.level === "ineligible";
            return (
              <div key={i}
                className={`flex items-start gap-3 rounded-xl border p-4 ${
                  isBlock ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"
                }`}>
                {isBlock ? <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                         : <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />}
                <p className={`text-sm leading-relaxed ${isBlock ? "text-red-800" : "text-amber-800"}`}>{w.message}</p>
              </div>
            );
          })}
        </div>
      )}

      {blocking && (
        <div className="mb-8 rounded-xl bg-[#f3ede2] border border-[#e0d3ba] p-4 text-sm text-[#6b5228]">
          Votre profil présente un point de blocage pour l'éligibilité. Les solutions ci-dessous restent indicatives — une alternative peut être envisagée (co-emprunteur, apport, paiement comptant).
        </div>
      )}

      {recommendations.length === 0 && (
        <div className="rounded-2xl border border-border bg-white p-8 text-center" data-testid="no-solution">
          <XCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <p className="font-heading font-bold text-lg text-[#2b2b2b]">Aucune solution de financement</p>
          <p className="text-sm text-muted-foreground mt-1">
            Votre profil ne permet pas de proposer de financement en ligne. Un paiement comptant ou un co-emprunteur éligible peut être envisagé.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {recommendations.map((r, idx) => (
          <motion.div key={r.id + idx}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.07 }}
            data-testid={`solution-card-${r.id}`}
            className="rounded-2xl border border-border bg-white overflow-hidden shadow-sm">
            <div className="flex items-start justify-between gap-3 p-5 sm:p-6 bg-[#faf7f1] border-b border-border">
              <div className="flex items-start gap-4">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#9E7B41] text-white shrink-0">
                  <Award className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-heading font-bold text-lg text-[#2b2b2b]">{r.name}</h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <Badge className="bg-[#434343] hover:bg-[#434343] text-white text-xs">{r.provider}</Badge>
                    <span className="text-xs text-muted-foreground">{r.category}</span>
                  </div>
                </div>
              </div>
              <Badge variant="outline" className="border-[#9E7B41] text-[#9E7B41] shrink-0 whitespace-nowrap">
                <Clock className="h-3 w-3 mr-1" /> {r.delai}
              </Badge>
            </div>

            {r.bareme_socram && <SocramLoaSimulator sol={{ ...r, prix_ttc: prixNum, apport: answers.apport_eur }} key={`soc-${r.id}-${answers.prix_remorque}`} bareme={r.bareme_socram} initial={initialMontant} prixInit={parseFloat(answers.prix_remorque) || initialMontant} apportVal={answers.apport_eur} onApportChange={setApportFromSim} solId={r.id} />}
            {r.bareme_fractionne && <FractionneSimulator sol={{ ...r, prix_ttc: prixNum, apport: answers.apport_eur }} key={`frac-${r.id}-${answers.montant}`} bareme={r.bareme_fractionne} initial={montant > 0 ? montant : 3000} solId={r.id} />}
            {r.bareme_sofinco && <SofincoCreditSimulator sol={{ ...r, prix_ttc: prixNum, apport: answers.apport_eur }} key={`sof-${r.id}-${answers.montant}`} bareme={r.bareme_sofinco} initial={initialMontant} pro={answers.client_type === "pro"} solId={r.id} />}
            {r.bareme_credit && <AmortSimulator sol={{ ...r, prix_ttc: prixNum, apport: answers.apport_eur }} key={`amo-${r.id}-${answers.montant}`} bareme={r.bareme_credit} initial={initialMontant} solId={r.id} />}
            {r.bareme && <CreditBailSimulator sol={{ ...r, prix_ttc: prixNum, apport: answers.apport_eur }} key={`cb-${r.id}-${answers.prix_remorque}`} bareme={r.bareme} prixInit={parseFloat(answers.prix_remorque) || initialMontant} solId={r.id} />}

            <div className="p-5 sm:p-6 grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-muted-foreground leading-relaxed">{r.modalites}</p>

                <p className="text-xs font-semibold tracking-wider uppercase text-[#9E7B41] mt-5 mb-2">Avantages</p>
                <ul className="space-y-1.5">
                  {r.avantages.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#2b2b2b]">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {a}
                    </li>
                  ))}
                </ul>

                {r.contraintes && r.contraintes.length > 0 && (
                  <>
                    <p className="text-xs font-semibold tracking-wider uppercase text-muted-foreground mt-5 mb-2">Conditions</p>
                    <ul className="space-y-1.5">
                      {r.contraintes.map((c, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#9E7B41] shrink-0 mt-2" /> {c}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <div className="rounded-xl bg-[#faf7f1] border border-border p-4">
                <p className="flex items-center gap-2 text-sm font-bold text-[#2b2b2b] mb-3">
                  <FileText className="h-4 w-4 text-[#9E7B41]" /> Pièces à préparer
                </p>
                <ul className="space-y-2" data-testid={`docs-${r.id}`}>
                  {r.docs.map((d, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-[#2b2b2b]">
                      <span className="h-4 w-4 rounded border-2 border-[#9E7B41]/50 shrink-0 mt-0.5" /> {d}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <Separator className="my-8" />

      <div className="rounded-2xl border border-border bg-white p-5 sm:p-6">
        <p className="flex items-center gap-2 font-heading font-bold text-[#2b2b2b] mb-3">
          <HeartHandshake className="h-5 w-5 text-[#9E7B41]" /> Règles & particularités
        </p>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
          {(result.eligibility_rules || []).map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-[#9E7B41] shrink-0 mt-2" /> {r}
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-2xl border border-[#9E7B41]/30 bg-[#faf7f1] p-5 sm:p-6 mt-8" data-testid="mentions-legales">
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          {MENTIONS_LEGALES}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mt-8 print:hidden">
        <Button data-testid="restart-simulation-button" onClick={onRestart}
          className="bg-[#9E7B41] hover:bg-[#876634] h-12 px-7 rounded-xl font-semibold">
          <RotateCcw className="h-4 w-4 mr-2" /> Nouvelle simulation
        </Button>
        <Button variant="outline" data-testid="result-export-excel-button" onClick={() => onExcel(compareDuree)}
          className="h-12 px-7 rounded-xl border-border">
          <Download className="h-4 w-4 mr-2" /> Télécharger la synthèse complète (Excel)
        </Button>
      </div>
    </motion.div>
  );
}
